import { Button } from "@/src/components/buttons";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { UploadCards } from "@/src/components/uploadSpots";
import { Exam, SimpleExam } from "@/src/data/allTypes";
import apiFetch, { sleep } from "@/src/utils/apiFetch";
import { formatDate, getItemId } from "@/src/utils/utils";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { GrTestDesktop } from "react-icons/gr";
import { ExamDocumentsCompartment } from "../documents/examDocumentsCompartment";
import { PatientExamForm } from "./patientExamForm";
import { sendSimpleNotification } from "../notifications/notificationsHook";
import { FieldAnswersList } from "../fields/fieldsAnswersList";

export async function openExamsVisualisationDrawer({
  exams,
  index,
  onEdit,
}: {
  exams: SimpleExam[];
  index: number;
  onEdit?: (exam: Exam) => void;
}) {
  let simpleExam = exams[index];
  if (!simpleExam) return;

  let exam = (await apiFetch(
    "exams",
    "loadById",
    {
      id: simpleExam.id,
    },
    "Erreur lors du chargement de l'examen"
  )) as Exam | null;
  if (!exam) throw new Error("Examen non trouvé");

  let documentTemplates = await apiFetch("documentTemplates", "loadMany", {
    referenceId: getItemId(exam.reference),
    types: ["standard", "letter"],
  });

  async function localOnEdit(newExam: Exam) {
    onEdit?.(newExam);
    exams[index] = newExam;
    await openExamsVisualisationDrawer({ exams, index: index });
  }

  let nextExam = exams[index + 1];
  let previousExam = exams[index - 1];
  closeDrawer("examVisualisation");
  await sleep(100);
  await openDrawer({
    id: "examVisualisation",
    title: (
      <ExamContentTitle exam={exam} onClick={async () => {}}></ExamContentTitle>
    ),
    additionalClasses: ["small"],
    content: (
      <div className="c1 gap20">
        <ExamContentDrawer
          exam={exam}
          onClick={async () => {
            if (!exam) return;
            await openExamEditDrawer({
              examId: exam.id,
              onConfirm: localOnEdit,
            });
          }}
        />
        {documentTemplates?.length != 0 && exam.status == "finished" && (
          <div className="mtop20">
            <ExamDocumentsCompartment exam={exam} patient={exam.patient!} />
          </div>
        )}
      </div>
    ),

    footer: (
      <div className="c1 ">
        {/* <ExamVisualisationList
          exams={exams}
          selectedIndex={index}
          onSelect={async (newIndex, exam) => {
            await openExamsVisualisationDrawer({
              exams,
              index: newIndex,
              onEdit: localOnEdit,
            });
          }}
        ></ExamVisualisationList> */}
        <div className="c5 gap10 p15">
          <Button
            type={"tertiary"}
            isDisabled={!previousExam}
            onClick={async () => {
              await openExamsVisualisationDrawer({
                exams,
                index: index - 1,
                onEdit: localOnEdit,
              });
            }}
          >
            <FaChevronLeft size={15} />
          </Button>

          <div className="triple">
            <Button
              type={"tertiary"}
              onClick={async () => {
                if (!exam) return;
                await openExamEditDrawer({
                  examId: exam.id,
                  onConfirm: localOnEdit,
                });
              }}
            >
              {exam.status == "finished" ? "Modifier" : "Ajouter"}
            </Button>
          </div>

          <Button
            type={"tertiary"}
            isDisabled={!nextExam}
            onClick={async () => {
              await openExamsVisualisationDrawer({
                exams,
                index: index + 1,
                onEdit: localOnEdit,
              });
            }}
          >
            <FaChevronRight size={15} />
          </Button>
        </div>
      </div>
    ),
  });
}

export function ExamContentTitle({
  exam,
  onClick,
}: {
  exam: Exam;
  onClick: () => void;
}) {
  return (
    <div
      className="c1-auto gap10"
      onClick={() => {
        onClick?.();
      }}
    >
      <div>
        <div className="bold">{exam.reference?.name}</div>
        <div className="gray f14">{formatDate(exam._date, "LL HH:mm")}</div>
      </div>
    </div>
  );
}

export function ExamContentDrawer({
  exam,
  onClick,
}: {
  exam: Exam;
  onClick: () => void;
}) {
  if (exam.status != "finished")
    return (
      <div className="center" style={{ minHeight: "50vh" }}>
        <div className="c1 gap20 center">
          <GrTestDesktop size={40} color={"RGBA(0,0,0,0.3)"} />
          <div className="gray">Aucun résultat pour le moment</div>
          <Button onClick={onClick}>Ajouter un résultat</Button>
        </div>
      </div>
    );
  return (
    <div className="grid c1 gap10">
      <div className="c1 gap10 mtop10">
        <FieldAnswersList
          patient={exam.patient}
          reference={exam.reference}
          answers={exam?.content?.fieldAnswers || []}
        ></FieldAnswersList>
      </div>
      <div>
        <div className="">{exam.notes}</div>
      </div>
      <div>
        <div className="bold">{exam.conclusion}</div>
      </div>
      <div>
        <UploadCards uploads={exam.uploads} count={2} />
      </div>
    </div>
  );
}

export async function openExamEditDrawer({
  examId,
  onConfirm,
}: {
  examId: string | undefined | null;
  onConfirm: (exam: Exam) => void;
}) {
  if (!examId) return;
  let exam = (await apiFetch(
    "exams",
    "loadById",
    {
      id: examId,
    },
    "Erreur lors du chargement de l'examen"
  )) as Exam;

  if (!exam) return;

  await openDrawer({
    title: "Ajouter un examen",
    additionalClasses: ["medium"],
    content: (
      <PatientExamForm
        reference={exam.reference!}
        patient={exam.patient!}
        current={exam}
        onChange={(newValue) => {
          exam = newValue;
        }}
      />
    ),
    onConfirm: async () => {
      if (!exam.notes && !exam.conclusion && exam.uploads?.length == 0)
        exam.status = "pending";
      else {
        exam.status = "finished";
      }

      let item = await await apiFetch(
        "exams",
        "addOne",
        {
          exam,
        },
        "Erreur lors de l'enregistrement de l'examen"
      );

      if (item.status == "finished")
        sendSimpleNotification({
          item,
          type: "examResult",
        });

      onConfirm?.(item);
    },
  });
}
